import { useEffect, useState, useSyncExternalStore } from 'react';
import type { DisconnectReason, RemoteTrack, Room } from 'livekit-client';
import { ApiError, api, callsApi, type AppCall, type CallJoin } from '../api/client';
import { applyChosenDevices, devicesChanged, onDeviceChosen, savedDevice, switchRoomDevice } from './audioDevices';
import { useAuth } from './auth';
import { useMessagesLive } from './messages';
import { leavePatch, patchBusy } from './patch';

/**
 * Calls in the app: placed and answered here, carried by the call gateway, with the sound on
 * LiveKit. Kept outside React (like Patches) so a call stays up while the person moves around.
 *
 * - The line's calls come from the server (GET …/me/line/calls) and then live, as 'call' events on
 *   Messages' stream. Every open Stitch on the line rings; the one that answers gets the sound and
 *   the others show it was answered elsewhere. Each change has a number (seq): an older one heard
 *   late (two containers, a reconnect) is ignored.
 * - The sound: this device joins the call's LiveKit room (the gateway is the other participant),
 *   sending its microphone and playing what the phone system sends, from a hidden audio element
 *   marked private (live help and session recordings leave it out).
 * - Ringing, ringback and keypad tones are made here with Web Audio: no sound files.
 * - Several people on one call (a conference): each one added is a call of the person's own whose
 *   sound is in the first call's room, which this device is already in. The calls that share a
 *   `conference` are shown as one call with everyone listed; this device's `mine` is any one of
 *   them, and moves to another when one ends, without touching the sound.
 * - The speaker and microphone chosen (lib/audioDevices) are used for the call's sound.
 */

type LiveKit = typeof import('livekit-client');
let lk: LiveKit | null = null;
const sdk = async (): Promise<LiveKit> => (lk ??= await import('livekit-client'));

export interface CallsState {
  /** The company whose line this is ('' until a page for one is open). */
  tenant: string;
  extension: string;
  /** This person's id, to tell which device answered. */
  userId: string;
  /** Calls in the app are meant for this company (else nothing about them shows). */
  on: boolean;
  /** Calls in the app work at this company; when not, why, in plain words. */
  available: boolean;
  reason: string | null;
  /** The line's calls now (ringing, dialing, connected), oldest first. */
  calls: AppCall[];
  /** The call this device has the sound for (it placed or answered it). */
  mine: string | null;
  audio: 'off' | 'connecting' | 'connected' | 'reconnecting';
  muted: boolean;
  micProblem: string | null;
  /** The browser is holding the sound back until the person clicks. */
  needsClick: boolean;
  /** When each call connected, on this device's clock. */
  since: Record<string, number>;
  keypad: boolean;
  /** The transfer panel is open (a number to send the call to: straight on, or after talking first). */
  transferring: boolean;
  /** The Add someone panel is open (a number or extension to bring into the call). */
  adding: boolean;
  /** The speaker and microphone choice is open. */
  sound: boolean;
  /** A chosen speaker or microphone that isn't there any more, in words (the default is used). */
  deviceNotice: string | null;
  /** Something to tell the person (a call that failed, ended or couldn't start). */
  notice: string | null;
  /** An action on its way: 'dial', or 'answer:<id>' and the like. */
  busy: string | null;
  /**
   * Blind transfers that come back (2714), by the caller's call: who is being rung and until when,
   * or (`until` null) that nobody answered and the caller is back on this line, on hold.
   */
  recall: Record<string, { name: string; until: number | null }>;
  /** The calls ringing those people, by their id: the caller's call each is for (not a "talking first" consult). */
  recallConsults: Record<string, string>;
}

const START: CallsState = {
  tenant: '',
  extension: '',
  userId: '',
  on: false,
  available: false,
  reason: null,
  calls: [],
  mine: null,
  audio: 'off',
  muted: false,
  micProblem: null,
  needsClick: false,
  since: {},
  keypad: false,
  transferring: false,
  adding: false,
  sound: false,
  deviceNotice: null,
  notice: null,
  busy: null,
  recall: {},
  recallConsults: {}
};

let state: CallsState = START;
const listeners = new Set<() => void>();
/** Calls that have ended (a late change about one is ignored), with how each ended. */
const ended = new Set<string>();
const endings = new Map<string, AppCall>();
/** Consult calls whose transfer the person completed (their ending says "transferred", and there's no going back). */
const completing = new Set<string>();
/**
 * Calls this device is transferring straight on: however their ending arrives first (the stream,
 * or a reload that finds the call gone and can't say why), it says "Call transferred.".
 */
const handedOff = new Set<string>();
/** Calls this device placed or answered (an outgoing one has no "answered by" to tell). */
const owned = new Set<string>();
/** People this device is taking off its call (Remove, or Cancel while they ring): their ending says nothing. */
const removing = new Set<string>();
/**
 * Calls with several people this device just left (they carry on) or ended for everyone (on their
 * way out), by call: until the gateway's word arrives, they show that way and not as this device's.
 */
const offHere = new Map<string, 'left' | 'ended'>();
/**
 * Blind transfers that come back (2714), by the consult call ringing the person: the caller's call
 * (holding), who is rung, the timer that brings the caller back, and who to tell how it ended.
 */
const recalls = new Map<string, { from: string; name: string; timer: ReturnType<typeof setTimeout>; onEnd?: ((how: 'answered' | 'came back') => void) | undefined }>();

function set(next: Partial<CallsState>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
  tones();
  notifyRinging();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The calls now, and every change (headset buttons and lights, 3141). */
export const callsNow = (): CallsState => state;
export const onCallsState = subscribe;

/** The calls on this person's line, for the dock and the dial pad. */
export function useCalls(): CallsState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** On a call in the Stitch app right now (this device or another of theirs): a team text's sound is one soft tone then (1.12.33). */
export function onAppCall(): boolean {
  return state.calls.some((c) => c.state === 'active' && !leftCall(c));
}

/** This open Stitch (one tab, or the installed app): the device that answers. */
let device = '';
export function deviceId(): string {
  if (device) return device;
  const make = () => {
    const a = new Uint8Array(9);
    crypto.getRandomValues(a);
    return `web-${[...a].map((b) => (b % 36).toString(36)).join('')}`;
  };
  try {
    const kept = sessionStorage.getItem('stitch.device');
    device = kept && /^[A-Za-z0-9-]{4,40}$/.test(kept) ? kept : make();
    sessionStorage.setItem('stitch.device', device);
  } catch {
    device = make();
  }
  return device;
}

const myDevice = () => `${state.userId}:${deviceId()}`;

/** How many people a call can have besides the person (the call gateway's limit). */
export const CONFERENCE_MAX = 5;

/** The call several calls make together (a conference: its first call's id), or a call's own id. */
export const confKey = (c: AppCall): string => c.conference ?? c.id;

/** Everyone on a call: the line's live calls that make it up, the first first. */
export function peopleOn(key: string, calls: AppCall[] = state.calls): AppCall[] {
  return calls.filter((c) => confKey(c) === key && c.state !== 'ended');
}

/** Groups calls into the calls they make up (a conference's together), in order. */
export function callGroups(calls: AppCall[]): AppCall[][] {
  const groups = new Map<string, AppCall[]>();
  for (const c of calls) groups.set(confKey(c), [...(groups.get(confKey(c)) ?? []), c]);
  return [...groups.values()];
}

/** A call this person left: it's going on without them (the gateway says so, or this device just did it). */
export const leftCall = (c: AppCall): boolean => Boolean(c.left) || offHere.get(confKey(c)) === 'left';

/** A call this device just ended for everyone: on its way out, so nothing shows it. */
export const endingHere = (c: AppCall): boolean => offHere.get(confKey(c)) === 'ended';

/** A call this device has (placed or answered here, or one of the people on it). */
export const isMine = (c: AppCall) => {
  if (leftCall(c) || endingHere(c)) return false;
  if (state.mine === c.id || owned.has(c.id) || (c.answeredBy !== null && c.answeredBy === myDevice())) return true;
  const mine = state.mine ? state.calls.find((x) => x.id === state.mine) : undefined;
  return Boolean(mine && c.conference && confKey(mine) === c.conference);
};

// --- coworkers' names, for the people on a call (the company directory) --------------------------------

const directories = new Map<string, Map<string, string>>();
const directoryLoading = new Set<string>();
/** Loads the company's people once (extension → name), for names on a call and Add someone. */
export function loadCoworkers(tenant: string): void {
  const t = tenant.toLowerCase();
  if (!t || directories.has(t) || directoryLoading.has(t)) return;
  directoryLoading.add(t);
  api
    .directory(t)
    .then((d) => {
      directories.set(t, new Map(d.entries.filter((e) => e.kind === 'person' && e.name).map((e) => [e.extension, e.name])));
      set({});
    })
    .catch(() => directories.set(t, new Map()))
    .finally(() => directoryLoading.delete(t));
}

/** The company's people with an extension, for Add someone's suggestions. */
export function coworkers(tenant: string): { extension: string; name: string }[] {
  return [...(directories.get(tenant.toLowerCase())?.entries() ?? [])].map(([extension, name]) => ({ extension, name }));
}

/** Who a call is with: their name (a coworker's from the directory), or the number. */
export function whoOf(c: AppCall): string {
  return c.name || (c.number ? directories.get(c.tenant.toLowerCase())?.get(c.number) : undefined) || prettyNumber(c.number);
}

/** Turns "(212) 555-0100" into what's dialed. Null: nothing that can be dialed. */
export function dialable(raw: string): string | null {
  const s = raw.trim().replace(/[\s().-]/g, '');
  return /^\+?[0-9*#]{1,32}$/.test(s) ? s : null;
}

/** "(212) 555-0100" for ten US digits, "+1 (212) 555-0100" for eleven; anything else as it is. */
export function prettyNumber(n: string | null | undefined): string {
  if (!n) return 'Unknown number';
  const d = n.replace(/^\+/, '');
  if (/^\d{10}$/.test(d)) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (/^1\d{10}$/.test(d)) return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return n;
}

/** Why an outgoing call didn't connect, from the phone system's answer. */
export function failedText(c: AppCall): string {
  const code = c.event.code ?? 0;
  if (code === 486 || code === 600) return 'That number is busy.';
  if (code === 404 || code === 484 || code === 604) return 'That number doesn’t exist, or couldn’t be reached.';
  if (code === 480 || code === 408) return 'Nobody picked up.';
  if (code === 403) return 'The phone system didn’t allow that call.';
  if (code === 401 || code === 407) return 'The phone system refused your line’s login. Stitch sets it again shortly; your desk phone still works.';
  if (code === 503 || code === 502 || code === 500) return 'The phone system couldn’t place the call just now. Try again.';
  if (code === 487 || c.event.cause === 'cancelled') return 'Call cancelled.';
  return 'The call didn’t go through.';
}

function messageOf(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

// --- the line: what the server says, then live -------------------------------------------------------

let loadedFor = '';
async function load(tenant: string): Promise<void> {
  try {
    const r = await callsApi.line(tenant);
    if (state.tenant !== tenant) return;
    set({ on: r.on, available: r.available, reason: r.reason ?? null });
    // What's on the line now replaces what we thought (anything gone has ended).
    const now = new Set(r.calls.map((c) => c.id));
    for (const c of state.calls) if (!now.has(c.id)) take({ ...c, state: 'ended', seq: c.seq + 1, event: { type: 'ended', cause: 'unknown' } });
    for (const c of r.calls) take(c);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'CANCELLED') {
      loadedFor = '';
      return;
    }
    if (state.tenant === tenant) set({ available: false, reason: err instanceof ApiError && err.status === 403 ? null : messageOf(err) });
  }
}

/** The clocks still needed: each live call's, and each conference's (from its first call's start). */
function clocksFor(calls: AppCall[], since: Record<string, number>): Record<string, number> {
  const keys = new Set<string>();
  for (const x of calls) {
    keys.add(x.id);
    if (x.conference) keys.add(x.conference);
  }
  return Object.fromEntries(Object.entries(since).filter(([k]) => keys.has(k)));
}

/** One change to a call: kept if it's newer than what's here. */
function take(c: AppCall): void {
  if (c.tenant !== state.tenant || ended.has(c.id)) return;
  const cur = state.calls.find((x) => x.id === c.id);
  if (cur && c.seq <= cur.seq) return;
  if (c.state === 'ended') {
    ended.add(c.id);
    owned.delete(c.id);
    recallEnded(c, cur);
    endings.set(c.id, c);
    if (endings.size > 50) endings.delete(endings.keys().next().value!);
    const calls = state.calls.filter((x) => x.id !== c.id);
    const since = clocksFor(calls, state.since);
    const removed = removing.delete(c.id);
    // One of the people on this device's call left (or was taken off, or didn't answer): the others
    // carry on, and so does the sound (it's one room). This device's call moves to one still on.
    const conf = c.conference ?? cur?.conference ?? null;
    const myCall = state.mine ? state.calls.find((x) => x.id === state.mine) : undefined;
    const rest = conf ? peopleOn(conf, calls) : [];
    if (!peopleOn(confKey(c), calls).length) offHere.delete(confKey(c));
    if (conf && rest.length && myCall && confKey(myCall) === conf && !(c.left || cur?.left)) {
      const who = whoOf(cur ?? c);
      const notice = removed ? state.notice : c.direction === 'outbound' && !state.since[c.id] ? `${who} wasn’t added. ${failedText(c)}` : `${who} left the call.`;
      const mine = state.mine === c.id ? (rest.find((x) => x.state === 'active') ?? rest[0]!).id : state.mine;
      set({ calls, since, notice, mine });
      if (notice !== null && !removed) later(() => state.notice === notice && set({ notice: null }), 5000);
      return;
    }
    const mine = state.mine === c.id;
    let notice = state.notice;
    // A consult that ended by itself (no answer, busy, they hung up): back to the caller, who's on hold.
    const from = c.consultFor ?? cur?.consultFor ?? null;
    const done = completing.delete(c.id);
    const handed = handedOff.delete(c.id);
    const back = mine && from && !done ? from : null;
    if (mine) {
      notice = done || handed || c.event.cause === 'transferred' ? 'Call transferred.' : c.direction === 'outbound' && !state.since[c.id] ? failedText(c) : 'Call ended.';
      if (back) notice = `${notice} You’re back with the caller.`;
      void dropAudio();
    }
    set({ calls, since, notice, ...(mine ? { mine: null, keypad: false, transferring: false, adding: false, sound: false } : {}) });
    if (back) void backTo(back);
    if (mine) later(() => state.notice === notice && set({ notice: null }), 5000);
    return;
  }
  // A transfer straight on that the far end turned down: the call stays.
  if (c.event.type === 'transfer' && c.event.transferState === 'failed') {
    handedOff.delete(c.id);
    if (state.mine === c.id) set({ notice: 'The transfer didn’t go through. You’re still on the call.' });
  }
  let since = state.since;
  if (c.state === 'active' && !since[c.id]) since = { ...since, [c.id]: Date.now() };
  // A conference's clock runs from its first call's start.
  if (c.state === 'active' && c.conference && !since[c.conference]) since = { ...since, [c.conference]: since[c.id]! };
  set({ calls: cur ? state.calls.map((x) => (x.id === c.id ? c : x)) : [...state.calls, c], since });
  // A blind transfer that comes back: the person answered, so the two are joined now.
  if (c.state === 'active' && recalls.has(c.id)) void joinRecall(c.id);
  // Take it here (Ask by message, 1.13.0): the call reception sends is answered on this device.
  if (!cur && c.state === 'ringing' && c.direction === 'inbound' && expected && expected.until > Date.now() && sameCaller(expected.number, c.number)) {
    expected = null;
    void answer(c.id);
  }
}

/** A call this device answers when it rings (Take it here), by the caller's number, for a while. */
let expected: { number: string; until: number } | null = null;
const lastTen = (n: string | null | undefined) => (n ?? '').replace(/[^0-9]/g, '').slice(-10);
function sameCaller(a: string, b: string | null): boolean {
  const x = lastTen(a);
  return Boolean(x) && x === lastTen(b);
}

/** Answer the next call from this number that rings this line in the next `ms` (Take it here). */
export function expectCall(number: string, ms = 30_000): void {
  expected = number ? { number, until: Date.now() + ms } : null;
}

function later(fn: () => void, ms: number): void {
  window.setTimeout(fn, ms);
}

/**
 * Keeps this person's line at a company in step: the calls on it now, and every change live.
 * Called by the company's pages; the line stays (and a call stays up) when they move on.
 */
export function useCallLine(tenant: string): void {
  const { state: auth } = useAuth();
  const key = tenant.toLowerCase();
  const m = auth?.memberships?.find((x) => x.tenant === key);
  const extension = m?.extension ?? '';
  const userId = auth?.user?.id ?? '';
  const on = Boolean(auth?.signedIn && extension && userId);
  useEffect(() => {
    if (!on) return;
    if (state.tenant !== key || state.extension !== extension) {
      // Another company's line: a call on the old one (if any) carries on in the dock.
      if (!state.mine) set({ ...START, tenant: key, extension, userId });
      else return;
    } else if (state.userId !== userId) set({ userId });
    if (loadedFor !== key) {
      loadedFor = key;
      void load(key);
    }
  }, [on, key, extension, userId]);
  useMessagesLive((e) => {
    if (e.type === 'reset' || e.type === 'hello') {
      if (state.tenant === key) void load(key);
      return;
    }
    if (e.type === 'call' && e.call && e.tenant === key) take(e.call);
  }, on);
  // Signed out: hang up whatever this device had (or leave it, with several people on it).
  const signedOut = Boolean(auth && !auth.signedIn);
  useEffect(() => {
    if (signedOut && state.mine) void hangupOrLeave(state.mine);
  }, [signedOut]);
}

// --- the sound -------------------------------------------------------------------------------------

let room: Room | null = null;
let audioBox: HTMLDivElement | null = null;

function sounds(): HTMLDivElement {
  if (!audioBox || !audioBox.isConnected) {
    audioBox = document.createElement('div');
    audioBox.hidden = true;
    audioBox.setAttribute('aria-hidden', 'true');
    audioBox.setAttribute('data-private', '');
    audioBox.className = 'call-sounds';
    document.body.appendChild(audioBox);
  }
  return audioBox;
}

function micText(err: unknown): string {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return 'This page can’t use a microphone: it needs a secure (https) connection.';
  if (!lk) return 'Your microphone couldn’t start. Press Unmute to try again.';
  const { MediaDeviceFailure } = lk;
  switch (MediaDeviceFailure.getFailure(err)) {
    case MediaDeviceFailure.PermissionDenied:
      return 'Your browser is blocking the microphone for Stitch, so the other person can’t hear you. Allow it in the site settings (by the address bar), then press Unmute.';
    case MediaDeviceFailure.NotFound:
      return 'No microphone was found. Plug one in, then press Unmute.';
    case MediaDeviceFailure.DeviceInUse:
      return 'Another app is using your microphone. Close it, then press Unmute.';
    default:
      return 'Your microphone couldn’t start. Press Unmute to try again.';
  }
}

async function setMic(on: boolean): Promise<void> {
  const r = room;
  if (!r) return;
  // The microphone chosen on this browser (one that's gone: the browser's own, and the device check says so).
  const id = on ? savedDevice('audioinput') : '';
  try {
    await r.localParticipant.setMicrophoneEnabled(on, id ? { deviceId: id } : undefined);
    if (room === r) set({ muted: !on, micProblem: null });
  } catch (err) {
    if (room === r) set({ muted: true, micProblem: micText(err) });
  }
}

// A speaker or microphone chosen during the call: the call's sound moves to it.
onDeviceChosen((kind, id) => {
  const r = room;
  if (!r) return;
  void switchRoomDevice(r, kind, id).then(
    () => room === r && set({ deviceNotice: null }),
    () => room === r && set({ deviceNotice: kind === 'audiooutput' ? 'That speaker couldn’t be used. Choose another.' : 'That microphone couldn’t be used. Choose another.' })
  );
});

/** Joins the call's room: the gateway is there (or will be when the call connects). */
async function joinAudio(callId: string, join: CallJoin): Promise<void> {
  await dropAudio();
  let L: LiveKit;
  try {
    L = await sdk();
  } catch {
    throw new Error('The call’s sound couldn’t load. Check your connection.');
  }
  if (state.mine !== callId) return;
  const r = new L.Room({
    adaptiveStream: false,
    dynacast: false,
    // A call isn't dropped by the page alone: hanging up does that (and pagehide sends one).
    disconnectOnPageLeave: false,
    audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
  });
  room = r;
  set({ audio: 'connecting' });
  const { RoomEvent, Track } = L;
  r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
    if (track.kind === Track.Kind.Audio) sounds().appendChild(track.attach());
  });
  r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    for (const el of track.detach()) el.remove();
  });
  r.on(RoomEvent.AudioPlaybackStatusChanged, () => room === r && set({ needsClick: !r.canPlaybackAudio }));
  r.on(RoomEvent.MediaDevicesError, (err: Error) => room === r && set({ micProblem: micText(err) }));
  // A headset plugged in or out: the lists follow, and a chosen one that went falls back to the default.
  r.on(RoomEvent.MediaDevicesChanged, () => {
    if (room !== r) return;
    void devicesChanged(r).then((gone) => room === r && gone && set({ deviceNotice: gone }));
  });
  r.on(RoomEvent.Reconnecting, () => room === r && set({ audio: 'reconnecting' }));
  r.on(RoomEvent.SignalReconnecting, () => room === r && set({ audio: 'reconnecting' }));
  r.on(RoomEvent.Reconnected, () => room === r && set({ audio: 'connected' }));
  r.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    if (room !== r) return;
    room = null;
    audioBox?.replaceChildren();
    set({ audio: 'off' });
    // Lost for good (not hung up here): the call can't be heard any more, so it ends (with several
    // people on it, this person leaves and the others carry on).
    const lost = state.mine;
    if (reason !== L.DisconnectReason.CLIENT_INITIATED && lost && (lost === callId || sameCall(lost, callId))) {
      const many = peopleOn(confKeyOf(lost)).length > 1;
      void hangupOrLeave(lost).then(() => set({ notice: many ? 'The connection to the call was lost, so you left it.' : 'The connection to the call was lost, so it ended.' }));
    }
  });
  try {
    await r.connect(join.url, join.token);
  } catch {
    // Dropped while connecting (the call ended, or was hung up): nothing went wrong with the sound.
    if (room !== r) return;
    room = null;
    r.removeAllListeners();
    set({ audio: 'off' });
    throw new Error('Couldn’t connect the call’s sound. Check your connection.');
  }
  if (room !== r) {
    void r.disconnect();
    return;
  }
  set({ audio: 'connected', needsClick: !r.canPlaybackAudio });
  await setMic(true);
  // The speaker and microphone chosen on this browser (listed now the microphone is allowed).
  const gone = await applyChosenDevices(r).catch(() => null);
  if (room === r) set({ deviceNotice: gone });
}

async function dropAudio(): Promise<void> {
  const r = room;
  room = null;
  audioBox?.replaceChildren();
  if (r) {
    r.removeAllListeners();
    await r.disconnect().catch(() => undefined);
  }
  if (state.audio !== 'off' || state.muted || state.micProblem || state.deviceNotice) set({ audio: 'off', muted: false, micProblem: null, needsClick: false, deviceNotice: null });
}

/**
 * The sound for a call this device just placed or answered. When it can't connect, the call is
 * hung up (nobody could hear it) and the reason stays on screen. A call that ended meanwhile
 * (busy, the caller hung up) keeps its own ending's words.
 */
async function connectSound(id: string, join: CallJoin | null): Promise<void> {
  if (!join) {
    set({ notice: 'The call is going, but its sound isn’t set up here.' });
    return;
  }
  try {
    await joinAudio(id, join);
  } catch (err) {
    if (ended.has(id) || state.mine !== id) return;
    set({ mine: null, keypad: false, notice: messageOf(err) });
    await callsApi.act(state.tenant, id, 'hangup').catch(() => undefined);
  }
}

// --- what the person does ----------------------------------------------------------------------------

/** Calls a number (or an extension) from this device. */
export async function dial(raw: string): Promise<boolean> {
  const to = dialable(raw);
  if (!to) {
    set({ notice: 'Enter a number to call: digits, * and #.' });
    return false;
  }
  if (!state.tenant || state.busy) return false;
  set({ busy: 'dial', notice: null, keypad: false, transferring: false });
  armTones();
  if (patchBusy()) await leavePatch();
  try {
    // On a call already: it goes on hold (everyone on it), and stays in the call bar to switch back to.
    const cur = state.mine ? state.calls.find((c) => c.id === state.mine) : undefined;
    if (cur) {
      if (cur.state === 'active' && anyoneTalking(cur)) take((await callsApi.act(state.tenant, cur.id, 'hold')).call);
      else if (cur.state !== 'active') {
        set({ notice: 'Wait for this call to connect, or hang up, before calling someone else.' });
        return false;
      }
      await dropAudio();
      set({ mine: null });
    }
    const out = await callsApi.dial(state.tenant, to, deviceId());
    // Over already (busy, no such number): the stream said so before the answer got here.
    if (ended.has(out.call.id)) {
      const how = endings.get(out.call.id);
      set({ notice: how ? failedText(how) : 'The call didn’t go through.' });
      return true;
    }
    owned.add(out.call.id);
    // Placed: the buttons are free again while the sound connects (another call, hang up, hold).
    set({ mine: out.call.id, busy: null });
    take(out.call);
    await connectSound(out.call.id, out.join);
    return true;
  } catch (err) {
    set({ notice: messageOf(err) });
    return false;
  } finally {
    if (state.busy === 'dial') set({ busy: null });
  }
}

/** Answers a ringing call on this device. */
export async function answer(id: string): Promise<void> {
  if (!state.tenant || state.busy) return;
  set({ busy: `answer:${id}`, notice: null });
  armTones();
  if (patchBusy()) await leavePatch();
  // Another call of this device's goes on hold first (the gateway does it too).
  try {
    const out = await callsApi.answer(state.tenant, id, deviceId());
    if (ended.has(id)) {
      set({ notice: 'The caller hung up.' });
      return;
    }
    owned.add(id);
    // Answered: the buttons are free again while the sound connects.
    set({ mine: id, busy: null });
    take(out.call);
    await connectSound(id, out.join);
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `answer:${id}`) set({ busy: null });
  }
}

/**
 * Someone a Patch rang calls back while it's going (1.12.18): Bring into Patch answers the call into
 * the Patch this person is in, as the caller ("Dana · on desk phone"). The card goes quietly.
 */
export async function bringIntoPatch(id: string): Promise<void> {
  if (!state.tenant || state.busy) return;
  set({ busy: `patch:${id}`, notice: null });
  try {
    await callsApi.bringIntoPatch(state.tenant, id, deviceId());
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `patch:${id}`) set({ busy: null });
  }
}

/** Sends a ringing call to voicemail (the phone system moves on, as when a desk phone declines). */
export async function decline(id: string): Promise<void> {
  set({ busy: `decline:${id}` });
  try {
    await callsApi.act(state.tenant, id, 'decline');
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    set({ busy: null });
  }
}

export async function hangup(id: string): Promise<void> {
  const tenant = state.tenant;
  if (state.mine === id) void dropAudio();
  try {
    await callsApi.act(tenant, id, 'hangup');
  } catch (err) {
    // Already over is fine.
    if (!(err instanceof ApiError && (err.code === 'NO_CALL' || err.code === 'CALL_ENDED'))) set({ notice: messageOf(err) });
  }
}

export async function hold(id: string, on: boolean): Promise<void> {
  set({ busy: `hold:${id}` });
  try {
    const out = await callsApi.act(state.tenant, id, on ? 'hold' : 'resume');
    take(out.call);
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    set({ busy: null });
  }
}

/**
 * Transfers this device's call straight on (blind): it leaves this line once the phone system has
 * taken it. The console sends it its own way (`send`: logged, or to someone's voicemail).
 */
export async function transfer(id: string, raw: string, send?: (tenant: string, id: string, to: string) => Promise<unknown>): Promise<boolean> {
  const to = dialable(raw);
  if (!to) {
    set({ notice: 'Enter a number or extension to transfer to: digits, * and #.' });
    return false;
  }
  if (state.busy) return false;
  set({ busy: `transfer:${id}`, notice: null });
  // Its ending can arrive before the answer does.
  handedOff.add(id);
  try {
    await (send ? send(state.tenant, id, to) : callsApi.transfer(state.tenant, id, to));
    // Taken already (its ending said so first): that word stays.
    set({ transferring: false, ...(ended.has(id) ? {} : { notice: `Transferring to ${prettyNumber(to)}…` }) });
    return true;
  } catch (err) {
    handedOff.delete(id);
    set({ notice: messageOf(err) });
    return false;
  } finally {
    set({ busy: null });
  }
}

/**
 * A warm transfer: the caller goes on hold and this device calls `raw`. Then completeTransfer joins
 * them, or cancelTransfer brings the caller back.
 */
export async function consult(id: string, raw: string): Promise<boolean> {
  const to = dialable(raw);
  if (!to) {
    set({ notice: 'Enter a number or extension to transfer to: digits, * and #.' });
    return false;
  }
  if (state.busy) return false;
  set({ busy: `consult:${id}`, notice: null });
  try {
    const out = await callsApi.consult(state.tenant, id, to, deviceId());
    if (ended.has(out.call.id)) {
      const how = endings.get(out.call.id);
      set({ transferring: false, notice: `${how ? failedText(how) : 'The call didn’t go through.'} You’re back with the caller.` });
      await backTo(id);
      return true;
    }
    await dropAudio();
    owned.add(out.call.id);
    set({ mine: out.call.id, transferring: false, keypad: false, busy: null });
    take(out.call);
    await connectSound(out.call.id, out.join);
    return true;
  } catch (err) {
    set({ notice: messageOf(err) });
    return false;
  } finally {
    if (state.busy === `consult:${id}`) set({ busy: null });
  }
}

/** Joins the caller and the person consulted; both calls leave this line. */
export async function completeTransfer(k: AppCall): Promise<void> {
  if (!k.consultFor || state.busy) return;
  completing.add(k.id);
  set({ busy: `complete:${k.id}` });
  try {
    await callsApi.complete(state.tenant, k.consultFor, k.id);
    set({ notice: 'Call transferred.' });
  } catch (err) {
    completing.delete(k.id);
    set({ notice: messageOf(err) });
  } finally {
    set({ busy: null });
  }
}

/** Ends the consult and goes back to the caller, with their sound. */
export async function cancelTransfer(k: AppCall): Promise<void> {
  if (!k.consultFor || state.busy) return;
  const from = k.consultFor;
  set({ busy: `cancel:${k.id}` });
  try {
    await dropAudio();
    const out = await callsApi.cancelConsult(state.tenant, from, k.id, deviceId());
    set({ mine: from, busy: null });
    take(out.call);
    await connectSound(from, out.join);
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `cancel:${k.id}`) set({ busy: null });
  }
}

// --- blind transfers that come back (2714) ---------------------------------------------------------------

/**
 * A blind transfer that comes back if nobody answers (the receptionist console, 2714): the caller
 * holds on this line while this device calls the person (a consult it doesn't listen to, so the
 * receptionist is free for the next call). When they answer, the two are joined at once; if nobody
 * answers within `seconds` (or the call fails), the ringing stops and the caller is back on this
 * line, still on hold. A person whose phone sends callers to voicemail answers that way, as with
 * any blind transfer.
 */
export async function transferWithRecall(id: string, raw: string, seconds: number, name: string, onEnd?: (how: 'answered' | 'came back') => void): Promise<boolean> {
  const to = dialable(raw);
  if (!to) {
    set({ notice: 'Enter a number or extension to transfer to: digits, * and #.' });
    return false;
  }
  if (state.busy) return false;
  set({ busy: `consult:${id}`, notice: null });
  try {
    const out = await callsApi.consult(state.tenant, id, to, deviceId());
    // The caller holds; this device listens to neither of them.
    if (state.mine === id) await dropAudio();
    const mine = state.mine === id ? null : state.mine;
    if (ended.has(out.call.id)) {
      // Over at once (busy, turned down): the caller is still here, on hold.
      const how = endings.get(out.call.id);
      set({ mine, recall: { ...state.recall, [id]: { name, until: null } }, notice: `${how ? failedText(how) : `${name} couldn’t be rung.`} The caller is back with you, on hold.` });
      onEnd?.('came back');
      return true;
    }
    owned.add(out.call.id);
    const timer = setTimeout(() => void recallNow(out.call.id), seconds * 1000);
    recalls.set(out.call.id, { from: id, name, timer, onEnd });
    set({
      mine,
      transferring: false,
      keypad: false,
      recall: { ...state.recall, [id]: { name, until: Date.now() + seconds * 1000 } },
      recallConsults: { ...state.recallConsults, [out.call.id]: id },
      notice: `Ringing ${name}. If they don’t answer in ${seconds} seconds, the caller comes back to you.`
    });
    take(out.call);
    return true;
  } catch (err) {
    set({ notice: messageOf(err) });
    return false;
  } finally {
    if (state.busy === `consult:${id}`) set({ busy: null });
  }
}

/** Forget a recall: its timer, and how the screens show it (the caller's word stays when it came back). */
function dropRecall(consultId: string, cameBack: boolean): { from: string; name: string; onEnd?: ((how: 'answered' | 'came back') => void) | undefined } | null {
  const r = recalls.get(consultId);
  if (!r) return null;
  recalls.delete(consultId);
  clearTimeout(r.timer);
  const { [consultId]: _gone, ...recallConsults } = state.recallConsults;
  const { [r.from]: _was, ...rest } = state.recall;
  set({ recallConsults, recall: cameBack ? { ...rest, [r.from]: { name: r.name, until: null } } : rest });
  return r;
}

/** The person answered: join them and the caller (both calls leave this line). */
async function joinRecall(consultId: string): Promise<void> {
  const r = recalls.get(consultId);
  if (!r) return;
  clearTimeout(r.timer);
  completing.add(consultId);
  try {
    await callsApi.complete(state.tenant, r.from, consultId);
    dropRecall(consultId, false);
    set({ notice: `${r.name} answered. Call transferred.` });
    r.onEnd?.('answered');
  } catch (err) {
    // Couldn't join them: end the ringing call and keep the caller.
    completing.delete(consultId);
    dropRecall(consultId, true);
    await callsApi.cancelConsult(state.tenant, r.from, consultId, deviceId()).then((out) => take(out.call)).catch(() => undefined);
    await holdQuietly(r.from);
    set({ notice: `${messageOf(err)} The caller is back with you, on hold.` });
    r.onEnd?.('came back');
  }
}

/** Nobody answered in time: stop the ringing; the caller is back on this line, on hold. */
async function recallNow(consultId: string): Promise<void> {
  const r = dropRecall(consultId, true);
  if (!r) return;
  try {
    const out = await callsApi.cancelConsult(state.tenant, r.from, consultId, deviceId());
    take(out.call);
  } catch {
    // Ended already (it was answered, or the caller left): its ending says so.
  }
  await holdQuietly(r.from);
  if (!ended.has(r.from)) set({ notice: `${r.name} didn’t answer. The caller is back with you, on hold.` });
  r.onEnd?.('came back');
}

/** Puts a call back on hold without a word (it was taken off hold when its transfer was given up). */
async function holdQuietly(id: string): Promise<void> {
  const call = state.calls.find((c) => c.id === id);
  if (!call || ended.has(id) || call.held || state.mine === id) return;
  try {
    take((await callsApi.act(state.tenant, id, 'hold')).call);
  } catch {
    // Gone, or already held.
  }
}

/** A call involved in a recall ended: the person's ringing call ended by itself, or the caller hung up. */
function recallEnded(c: AppCall, cur: AppCall | undefined): void {
  const r = recalls.get(c.id);
  if (r && !completing.has(c.id)) {
    // Busy, turned down, or no answer before the timer: the caller is still holding here.
    dropRecall(c.id, true);
    later(() => set({ notice: `${r.name} didn’t take it (${failedText(cur ?? c).replace(/\.$/, '')}). The caller is back with you, on hold.` }), 0);
    r.onEnd?.('came back');
  }
  // The caller hung up while the person rang: stop ringing them.
  for (const [consultId, x] of recalls) {
    if (x.from !== c.id || completing.has(consultId)) continue;
    dropRecall(consultId, false);
    void callsApi.act(state.tenant, consultId, 'hangup').catch(() => undefined);
  }
  if (state.recall[c.id]) {
    const { [c.id]: _gone, ...recall } = state.recall;
    set({ recall });
  }
}

/**
 * The receptionist takes the caller up again (or sends them somewhere else): a person still being
 * rung for them stops ringing (it counts as came back), and the "came back" word goes.
 */
export async function clearRecall(id: string): Promise<void> {
  for (const [consultId, x] of recalls) {
    if (x.from !== id) continue;
    dropRecall(consultId, false);
    await callsApi.act(state.tenant, consultId, 'hangup').catch(() => undefined);
    x.onEnd?.('came back');
  }
  if (!state.recall[id]) return;
  const { [id]: _gone, ...recall } = state.recall;
  set({ recall });
}

// --- several people on one call (a conference) ----------------------------------------------------------

/** Someone on this call is connected and not on hold (so Hold, or calling someone else, holds them). */
export function anyoneTalking(c: AppCall): boolean {
  return peopleOn(confKey(c)).some((x) => x.state === 'active' && !x.held);
}

/** The call a call id is part of on this line (its conference's key, or its own id). */
function confKeyOf(id: string): string {
  const c = state.calls.find((x) => x.id === id);
  return c ? confKey(c) : id;
}

/** Two call ids are one call for this device (the same conference, one room). */
function sameCall(a: string, b: string): boolean {
  return a === b || confKeyOf(a) === confKeyOf(b);
}

/**
 * Hold or Resume on the call card. Resuming a call whose sound isn't on this device (the call this
 * device had ended while this one waited on hold) switches to it, so its sound comes back here too.
 */
export function holdOrResume(id: string, on: boolean): Promise<void> {
  if (!on && !(state.mine && sameCall(state.mine, id))) return switchTo(id);
  return hold(id, on);
}

export const showAdd = (on: boolean) => {
  if (on) loadCoworkers(state.tenant);
  set({ adding: on, ...(on ? { keypad: false, transferring: false, sound: false } : {}) });
};

/**
 * Adds someone to this device's call: they're called from the person's line (a coworker's desk
 * phone, apps and phone all ring, as for any call) and, when they answer, everyone is on one call.
 */
export async function addToCall(id: string, raw: string): Promise<boolean> {
  const to = dialable(raw);
  if (!to) {
    set({ notice: 'Enter a number or extension to add: digits, * and #.' });
    return false;
  }
  if (!state.tenant || state.busy) return false;
  set({ busy: `add:${id}`, notice: null });
  loadCoworkers(state.tenant);
  try {
    const out = await callsApi.add(state.tenant, id, to);
    // Over already (busy, no such number): its ending said so first.
    if (!ended.has(out.call.id)) owned.add(out.call.id);
    set({ adding: false });
    take(out.call);
    return true;
  } catch (err) {
    set({ notice: messageOf(err) });
    return false;
  } finally {
    if (state.busy === `add:${id}`) set({ busy: null });
  }
}

/** Takes someone the person added off the call (Remove, or Cancel while it rings); the rest carry on. */
export async function removeFromCall(c: AppCall): Promise<void> {
  if (state.busy) return;
  removing.add(c.id);
  set({ busy: `remove:${c.id}` });
  try {
    // This device's sound is the whole call's room: it stays, on someone still there.
    if (state.mine === c.id) {
      const other = peopleOn(confKey(c)).find((x) => x.id !== c.id);
      if (other) set({ mine: other.id });
    }
    await callsApi.act(state.tenant, c.id, 'hangup');
  } catch (err) {
    removing.delete(c.id);
    if (!(err instanceof ApiError && (err.code === 'NO_CALL' || err.code === 'CALL_ENDED'))) set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `remove:${c.id}`) set({ busy: null });
  }
}

/** Off this device's call (left, or ended for everyone): its sound, and every part of it from what this device has. */
function offCall(key: string, how: 'left' | 'ended'): void {
  if (peopleOn(key).length) offHere.set(key, how);
  for (const m of peopleOn(key)) owned.delete(m.id);
  if (state.mine && confKeyOf(state.mine) === key) {
    void dropAudio();
    set({ mine: null, keypad: false, adding: false, transferring: false, sound: false });
  }
}

/**
 * Leaves a call with several people on it: the others carry on without this person. (Two are put
 * through to each other by the phone system; more stay on through Stitch, until one is left.)
 */
export async function leaveCall(id: string): Promise<void> {
  if (state.busy) return;
  const key = confKeyOf(id);
  const others = peopleOn(key).filter((c) => c.state === 'active').length;
  set({ busy: `leave:${id}` });
  try {
    await callsApi.act(state.tenant, id, 'leave');
    offCall(key, 'left');
    const notice = others >= 2 ? 'You left the call. The others are still talking.' : 'Call ended.';
    set({ notice });
    later(() => state.notice === notice && set({ notice: null }), 5000);
  } catch (err) {
    if (err instanceof ApiError && (err.code === 'NO_CALL' || err.code === 'CALL_ENDED')) offCall(key, 'ended');
    else set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `leave:${id}`) set({ busy: null });
  }
}

/** Ends a call with several people on it for all of them (whoever is still ringing too). */
export async function endForEveryone(id: string): Promise<void> {
  if (state.busy) return;
  const key = confKeyOf(id);
  const here = Boolean(state.mine && confKeyOf(state.mine) === key);
  set({ busy: `end:${id}` });
  try {
    await callsApi.act(state.tenant, id, 'end-all');
    offCall(key, 'ended');
    if (here) {
      const notice = 'Call ended for everyone.';
      set({ notice });
      later(() => state.notice === notice && set({ notice: null }), 5000);
    }
  } catch (err) {
    if (err instanceof ApiError && (err.code === 'NO_CALL' || err.code === 'CALL_ENDED')) offCall(key, 'ended');
    else set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `end:${id}`) set({ busy: null });
  }
}

/** Hangs up this device's call or, with several people on it, leaves it (closing Stitch, losing the sound, signing out). */
async function hangupOrLeave(id: string): Promise<void> {
  const key = confKeyOf(id);
  if (peopleOn(key).length <= 1) return hangup(id);
  offCall(key, 'left');
  try {
    await callsApi.act(state.tenant, id, 'leave');
  } catch (err) {
    if (!(err instanceof ApiError && (err.code === 'NO_CALL' || err.code === 'CALL_ENDED'))) set({ notice: messageOf(err) });
  }
}

/** Back to a call of this person's that's on hold: resumed, with its sound on this device. */
async function backTo(id: string): Promise<void> {
  const call = state.calls.find((c) => c.id === id);
  if (!call || ended.has(id)) return;
  try {
    // Everyone on it is taken off hold (one call, or a conference).
    if (peopleOn(confKey(call)).some((c) => c.state === 'active' && c.held)) take((await callsApi.act(state.tenant, id, 'resume')).call);
    const out = await callsApi.rejoin(state.tenant, id, deviceId());
    if (ended.has(id)) return;
    // Back on it: the buttons are free again while the sound connects.
    set({ mine: id, ...(state.busy === `switch:${id}` ? { busy: null } : {}) });
    take(out.call);
    await connectSound(id, out.join);
  } catch (err) {
    set({ notice: messageOf(err) });
  }
}

/** Switches to another of this person's calls that's on hold; the one they're on goes on hold. */
export async function switchTo(id: string): Promise<void> {
  if (state.busy) return;
  set({ busy: `switch:${id}`, notice: null });
  try {
    const cur = state.mine ? state.calls.find((x) => x.id === state.mine) : undefined;
    if (cur && !sameCall(cur.id, id)) {
      if (cur.state === 'active' && anyoneTalking(cur)) take((await callsApi.act(state.tenant, cur.id, 'hold')).call);
      await dropAudio();
      set({ mine: null });
    }
    await backTo(id);
  } catch (err) {
    set({ notice: messageOf(err) });
  } finally {
    if (state.busy === `switch:${id}`) set({ busy: null });
  }
}

export const showTransfer = (on: boolean) => set({ transferring: on, ...(on ? { keypad: false, adding: false, sound: false } : {}) });

let keys: Promise<unknown> = Promise.resolve();
/** A keypad press on a connected call: a tone here, and the digit to the far end (in order). */
export function pressKey(id: string, digit: string): void {
  if (!/^[0-9*#]$/.test(digit)) return;
  keyTone(digit);
  keys = keys.then(() => callsApi.dtmf(state.tenant, id, digit).catch((err) => set({ notice: messageOf(err) })));
}

export const toggleMute = () => setMic(state.muted || Boolean(state.micProblem));
export const showKeypad = (on: boolean) => set({ keypad: on, ...(on ? { transferring: false, adding: false, sound: false } : {}) });
/** The speaker and microphone choice on the call card. */
export const showSound = (on: boolean) => set({ sound: on, ...(on ? { keypad: false, transferring: false, adding: false } : {}) });
export const dismissCallNotice = () => set({ notice: null });

/** The browser held the sound back (no click yet): this click lets it play. */
export async function startCallSound(): Promise<void> {
  await room?.startAudio().catch(() => undefined);
  armTones();
}

// Closing the tab or the app mid-call hangs up (nobody would be left to hear it); with several people
// on the call, this person leaves it and the others carry on.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    const id = state.mine;
    if (!id || !state.tenant) return;
    void fetch(callsApi.hangupUrl(state.tenant, id, peopleOn(confKeyOf(id)).length > 1), {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json', 'x-stitch-request': '1' },
      body: '{}'
    }).catch(() => undefined);
  });
  window.addEventListener('beforeunload', (e) => {
    if (state.mine) e.preventDefault();
  });
}

// --- ringing, ringback and keypad tones (Web Audio) -----------------------------------------------------

type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
function context(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

let armed = false;
/** Browsers start sound only after a click or key in the page: be ready from the first one. */
function armTones(): void {
  const c = context();
  if (c && c.state !== 'running') void c.resume().catch(() => undefined);
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const wake = () => {
    const x = context();
    if (x && x.state !== 'running') void x.resume().catch(() => undefined);
  };
  for (const e of ['pointerdown', 'keydown', 'touchend'] as const) window.addEventListener(e, wake, { passive: true });
}
if (typeof window !== 'undefined') armTones();

/** Two tones together for `secs`, from `at` (context time). */
function beep(c: AudioContext, out: AudioNode, freqs: number[], at: number, secs: number, gain: number): void {
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.015);
  g.gain.setValueAtTime(gain, at + Math.max(0.02, secs - 0.03));
  g.gain.exponentialRampToValueAtTime(0.0001, at + secs);
  g.connect(out);
  for (const f of freqs) {
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    o.connect(g);
    o.start(at);
    o.stop(at + secs + 0.02);
  }
}

/**
 * One soft bell note: a quick start, then it fades like a struck chime (no steady tone). A quieter
 * octave above gives it warmth.
 */
function bell(c: AudioContext, out: AudioNode, f: number, at: number, secs: number, gain: number): void {
  for (const [mult, level] of [
    [1, gain],
    [2, gain * 0.18]
  ] as const) {
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(level, at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, at + secs);
    g.connect(out);
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.value = f * mult;
    o.connect(g);
    o.start(at);
    o.stop(at + secs + 0.02);
  }
}

/**
 * Waiting for people to join a Patch you started (1.12.26, Joey: "we should play a nice sound"):
 * three soft rising bell notes (G, C, E), then a rest. Gentle on purpose: unlike a phone's
 * ringback, it's never mistaken for a call.
 */
const PATCH_NOTES = [392, 523.25, 659.25];
function patchChime(c: AudioContext, out: AudioNode, t: number): void {
  PATCH_NOTES.forEach((f, i) => bell(c, out, f, t + i * 0.2, i === PATCH_NOTES.length - 1 ? 1.4 : 1.0, 0.16));
}

type Pattern = 'ring' | 'ringback' | 'waiting' | 'patch' | null;
let playing: Pattern = null;
let timer: number | undefined;
let master: GainNode | null = null;

/** One cycle of a pattern, scheduled now; how long until the next. */
function cycle(p: Exclude<Pattern, null>): number {
  const c = context();
  if (!c || c.state !== 'running') return 1000;
  if (!master) {
    master = c.createGain();
    master.gain.value = 0.5;
    master.connect(c.destination);
  }
  const t = c.currentTime + 0.03;
  if (p === 'ring') {
    // Stitch's ring: two short warbles, then a pause.
    for (const at of [0, 0.45]) for (let k = 0; k < 6; k++) beep(c, master, [k % 2 ? 932 : 784, k % 2 ? 1175 : 988], t + at + k * 0.055, 0.05, 0.35);
    return 2600;
  }
  if (p === 'patch') {
    patchChime(c, master, t);
    return 3400;
  }
  if (p === 'ringback') {
    // What a phone hears while the far end rings: 440 + 480 Hz, two seconds on, four off.
    beep(c, master, [440, 480], t, 2, 0.18);
    return 6000;
  }
  // Call waiting: one soft beep.
  beep(c, master, [440], t, 0.3, 0.15);
  return 10_000;
}

function play(p: Pattern): void {
  if (p === playing) return;
  window.clearTimeout(timer);
  playing = p;
  if (master) {
    master.disconnect();
    master = null;
  }
  if (!p) return;
  const run = () => {
    if (playing !== p) return;
    timer = window.setTimeout(run, cycle(p));
  };
  run();
}

/** Other things that ring like a call: a Patch from a direct message (lib/patchRing), by key. */
const otherRings = new Set<string>();
/** In a Patch: something ringing then is call waiting's soft beep, as on a call. */
let inAPatch = false;

/** Rings (or stops ringing) for something that isn't a phone call: a Patch someone patched you into. */
export function ringFor(key: string, on: boolean): void {
  if (on === otherRings.has(key)) return;
  if (on) otherRings.add(key);
  else otherRings.delete(key);
  tones();
}

/** This tab joined or left a Patch (lib/patch): ringing while in one is call waiting. */
export function busyInPatch(on: boolean): void {
  if (on === inAPatch) return;
  inAPatch = on;
  tones();
}

/** Waiting for people to join the Patch this person started (lib/patchWaiting). */
let patchWaiting = false;

/** The Patch this person started is waiting for the others (or no longer is): its soft chime. */
export function patchWaitingSound(on: boolean): void {
  if (on === patchWaiting) return;
  patchWaiting = on;
  tones();
}

/** From a click (Your settings → "Play"): the Patch waiting chime once. */
export async function previewPatchWaiting(): Promise<void> {
  const c = context();
  if (!c) return;
  if (c.state !== 'running') await c.resume().catch(() => undefined);
  if (c.state !== 'running') return;
  const g = c.createGain();
  g.gain.value = 0.5;
  g.connect(c.destination);
  patchChime(c, g, c.currentTime + 0.03);
  window.setTimeout(() => g.disconnect(), 2500);
}

/** Which sound fits now: ringing, call waiting, ringback while dialing out, a Patch waiting, or none. */
function tones(): void {
  if (typeof window === 'undefined') return;
  const ringing = otherRings.size > 0 || state.calls.some((c) => c.direction === 'inbound' && c.state === 'ringing');
  const mine = state.calls.find((c) => c.id === state.mine);
  if (ringing) play((mine && mine.state === 'active') || inAPatch ? 'waiting' : 'ring');
  else if (mine && mine.direction === 'outbound' && mine.state === 'dialing' && (mine.event.type === 'ringing' || mine.event.type === 'early_media')) play('ringback');
  else if (patchWaiting) play('patch');
  else play(null);
}

const KEY_FREQS: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477]
};
function keyTone(d: string): void {
  const c = context();
  const f = KEY_FREQS[d];
  if (!c || !f || c.state !== 'running') return;
  const g = c.createGain();
  g.gain.value = 0.25;
  g.connect(c.destination);
  beep(c, g, f, c.currentTime + 0.01, 0.12, 0.5);
  window.setTimeout(() => g.disconnect(), 400);
}

// --- a notification while Stitch isn't in front ---------------------------------------------------------

const shown = new Map<string, Notification>();
function notifyRinging(): void {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  const ringing = new Map(state.calls.filter((c) => c.direction === 'inbound' && c.state === 'ringing').map((c) => [c.id, c]));
  for (const [id, n] of shown) {
    if (!ringing.has(id)) {
      n.close();
      shown.delete(id);
    }
  }
  if (Notification.permission !== 'granted' || document.visibilityState === 'visible') return;
  for (const [id, c] of ringing) {
    if (shown.has(id)) continue;
    try {
      const n = new Notification(`Incoming call${c.name ? `: ${c.name}` : ''}`, { body: prettyNumber(c.number), tag: `call-${id}`, requireInteraction: true });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      shown.set(id, n);
    } catch {
      // Some browsers only show notifications from the service worker: the ring is still heard.
    }
  }
}

/** Seconds since a call connected, ticking (for "2:31"). */
export function useCallClock(id: string | null): number {
  const s = useCalls();
  const [now, setNow] = useState(() => Date.now());
  const from = id ? s.since[id] : undefined;
  useEffect(() => {
    if (!from) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [from]);
  return from ? Math.max(0, Math.floor((now - from) / 1000)) : 0;
}

export function clockText(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}
